import React, { useState, useRef } from "react";
import { Button, Box, Menu, MenuItem, ListItemText } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { ExpandMore } from "@mui/icons-material";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

interface AppModuleTabsProps {
  variant?: "navbar" | "card" | "minimal";
  maxVisibleItems?: number;
}

interface CategoryDef {
  name: string;
  path: string;
  permission?: string;
}

const ALL_CATEGORIES: CategoryDef[] = [
  { name: "SALES",  path: "/sales",             permission: PERMISSIONS.BOOKING_VIEW },
  { name: "OPS",    path: "/operations",  permission: PERMISSIONS.STREAM_VIEW },
  { name: "ADMIN",  path: "/user-management",    permission: PERMISSIONS.USER_MANAGEMENT_VIEW },
  { name: "SITES",  path: "/sites" },
  { name: "LEGACY", path: "/chema",              permission: PERMISSIONS.DONATION_VIEW },
  { name: "LMS",    path: "/courses",            permission: PERMISSIONS.ACADEMY_VIEW },
];

export const AppModuleTabs: React.FC<AppModuleTabsProps> = ({
  variant = "navbar",
  maxVisibleItems = 4,
}) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const { hasPermission } = usePermissions();

  const categories = ALL_CATEGORIES.filter(
    (c) => !c.permission || hasPermission(c.permission),
  );

  const showMore = categories.length > maxVisibleItems;
  const inlineCategories = showMore ? categories.slice(0, maxVisibleItems) : categories;
  const overflowCategories = showMore ? categories.slice(maxVisibleItems) : [];

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const isMoreActive = overflowCategories.some((c) =>
    location.pathname.startsWith(c.path),
  );

  const buttonSx = (isActive: boolean) => ({
    color: isActive ? "primary.main" : "text.primary",
    fontWeight: isActive ? "bold" : "medium",
    fontSize:
      variant === "card"
        ? "0.9rem"
        : { xs: "0.85rem", lg: "0.95rem" },
    textTransform: "none",
    minWidth: "auto",
    padding:
      variant === "card"
        ? "8px 16px"
        : { xs: "4px 8px", md: "6px 8px", lg: "6px 12px" },
    borderRadius: "6px",
    backgroundColor: isActive ? "action.selected" : "transparent",
    "&:hover": {
      backgroundColor: "action.hover",
      color: "primary.main",
    },
    ...(variant === "minimal" && { padding: "4px 8px", fontSize: "0.875rem" }),
  });

  return (
    <Box
      sx={{
        display: "flex",
        gap:
          variant === "card" ? 2 : { xs: 1.5, md: 2, lg: 3 },
        flexWrap: variant === "navbar" ? "nowrap" : "wrap",
        ...(variant === "card" && { justifyContent: "center", px: 2 }),
      }}
    >
      {inlineCategories.map((category) => {
        const isActive = location.pathname.startsWith(category.path);
        return (
          <Button
            key={category.name}
            component={Link}
            to={category.path}
            sx={buttonSx(isActive)}
          >
            {category.name}
          </Button>
        );
      })}

      {overflowCategories.length > 0 && (
        <>
          <Button
            ref={moreButtonRef}
            onClick={handleOpenMenu}
            endIcon={<ExpandMore />}
            sx={buttonSx(isMoreActive)}
          >
            More
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { mt: 1, minWidth: 160 } }}
          >
            {overflowCategories.map((category) => {
              const isActive = location.pathname.startsWith(category.path);
              return (
                <MenuItem
                  key={category.name}
                  component={Link}
                  to={category.path}
                  onClick={handleCloseMenu}
                  sx={{
                    color: isActive ? "primary.main" : "text.primary",
                    fontWeight: isActive ? "bold" : "normal",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <ListItemText
                    primary={category.name}
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </Box>
  );
};
